import { createAsyncThunk } from '@reduxjs/toolkit';

import { AppAnalyticsSnowflakeService, SnowflakeQueries } from '@/pages/AppAnalytics/AppAnalyticsSnowflakeService';
import { FuelEntityType, FuelPortalService } from '@/services';
import { getCountryName } from '@/utils/countryUtils';
import { dateUtils } from '@/utils/dateUtils';

import type { ExportAnalyticsJob } from './ExportAnalyticsJob';
import { removeJob, updateJob } from './ExportAnalyticsState';

const BATCH_SIZE = 100;
const DATE_FORMAT = 'MMM/dd/yyyy';

export const processExportContentAnalyticsJob = createAsyncThunk(
  'exportAnalyticsJobs/processExportContentAnalyticsJob',
  async (job: ExportAnalyticsJob, { dispatch, getState }) => {
    const service = AppAnalyticsSnowflakeService();
    const { params } = job;
    const range = {
      from: dateUtils.format(params.fromDate, params.timezone, DATE_FORMAT),
      to: dateUtils.format(params.toDate, params.timezone, DATE_FORMAT),
    };

    const updateMessage = (message: string) => {
      job.message = message;
      dispatch(updateJob(job));
    };

    job.progress = 0;
    dispatch(updateJob(job));
    updateMessage(`Requesting Snowflake data...`);

    const data = await service.getData(SnowflakeQueries.content, params);
    const total = data.length;
    const map = {};
    const clipIds = [];
    for (const row of data) {
      const key = row.video;
      if (key) {
        clipIds.push(key);
        map[key] = {
          views: row.count,
          watchTime: row.duration,
          averageWatchTime: row.average,
        };
      }
    }

    job.jobStatus = 'InProgress';
    job.progress = 20;
    dispatch(updateJob(job));

    // populate all clips data
    updateMessage(`Populating clip data`);
    let csv =
      'Title, ID, Type, Series, Season, Duration (seconds), Views, Watch Time (seconds), Average Watch Time (seconds), From (UTC), To (UTC)\n';
    let batch = [];
    for await (const clipId of clipIds) {
      if (batch.length === BATCH_SIZE) {
        const update = await processBatch(batch, map, range);
        csv += update;
        batch = [];

        const progress = (BATCH_SIZE / total) * 60 + 20;
        job.progress = progress;
        dispatch(updateJob(job));
      }
      batch.push(clipId);
    }
    const update = await processBatch(batch, map, range);
    csv += update;
    job.progress = 80;
    dispatch(updateJob(job));

    // export data as csv
    updateMessage(`Exporting data as CSV`);
    job.progress = 85;
    dispatch(updateJob(job));
    downloadCSV(csv, `content-analytics-${range.from}-to-${range.to}.csv`);

    // finalize
    clearInterval(job.intervalId);
    job.progress = 100;
    job.jobStatus = 'Completed';
    dispatch(updateJob(job));
    dispatch(removeJob(job));
    return true;
  }
);

export const processExportLiveChannelsAnalyticsJob = createAsyncThunk(
  'exportAnalyticsJobs/processExportLiveChannelsAnalyticsJob',
  async (job: ExportAnalyticsJob, { dispatch, getState }) => {
    const service = AppAnalyticsSnowflakeService();
    const { params } = job;
    const range = {
      from: dateUtils.format(params.fromDate, params.timezone, DATE_FORMAT),
      to: dateUtils.format(params.toDate, params.timezone, DATE_FORMAT),
    };

    const updateMessage = (message: string) => {
      job.message = message;
      dispatch(updateJob(job));
    };

    job.progress = 0;
    dispatch(updateJob(job));
    updateMessage(`Requesting Snowflake data...`);

    const data = await service.getData(SnowflakeQueries.liveChannel, params);

    job.jobStatus = 'InProgress';
    job.progress = 20;
    dispatch(updateJob(job));

    let csv = 'Channel, Views, Watch Time (seconds), Average Watch Time (seconds), From (UTC), To (UTC)\n';

    const progressRange = 60;
    for await (const row of data) {
      const channelId = row.channel;
      const channel = await FuelPortalService.getEntityById(FuelEntityType.Channel, channelId);
      const channelTitle = channel.name || 'Unknown Channel';

      let line = '';
      line += `${channelTitle},`;
      line += `${row.count},`;
      line += `${row.duration},`;
      line += `${row.average},`;
      line += `${range.from},${range.to}\n`;

      csv += line;

      const progress = (data.indexOf(row) / data.length) * progressRange + 20;
      job.progress = progress;
      dispatch(updateJob(job));
    }

    // export data as csv
    updateMessage(`Exporting data as CSV`);
    job.progress = 85;
    dispatch(updateJob(job));
    downloadCSV(csv, `live-channels-analytics-${range.from}-to-${range.to}.csv`);

    // finalize
    clearInterval(job.intervalId);
    job.progress = 100;
    job.jobStatus = 'Completed';
    dispatch(updateJob(job));
    dispatch(removeJob(job));
    return true;
  }
);

export const processExportWatchAnalyticsJob = createAsyncThunk(
  'exportAnalyticsJobs/processExportWatchAnalyticsJob',
  async (job: ExportAnalyticsJob, { dispatch, getState }) => {
    const service = AppAnalyticsSnowflakeService();
    const { params } = job;
    const range = {
      from: dateUtils.format(params.fromDate, params.timezone, DATE_FORMAT),
      to: dateUtils.format(params.toDate, params.timezone, DATE_FORMAT),
    };

    const updateMessage = (message: string) => {
      job.message = message;
      dispatch(updateJob(job));
    };

    job.progress = 0;
    dispatch(updateJob(job));
    updateMessage(`Requesting Snowflake data...`);

    const data = await service.getData(SnowflakeQueries.generic, params);

    job.jobStatus = 'InProgress';
    job.progress = 20;
    dispatch(updateJob(job));

    const map = [];
    let csv = 'Date, Site, Country, Device, Event Type, Views, Watch Time (seconds), Unique Visitors\n';

    const progressRange = 60;
    for (const row of data) {
      const date = row.eventdate;
      const site = row.site || '';
      const country = row.countrycode || '';
      const device = row.devicetype || '';
      const eventType = row.eventtype || '';

      if (!map[date]) {
        map[date] = {};
      }
      if (!map[date][site]) {
        map[date][site] = {};
      }
      if (!map[date][site][country]) {
        map[date][site][country] = {};
      }
      if (!map[date][site][country][device]) {
        map[date][site][country][device] = {};
      }
      if (!map[date][site][country][device][eventType]) {
        if (eventType === 'watch') {
          map[date][site][country][device][eventType] = {
            views: 0,
            duration: 0,
            uniqueViews: 0,
          };
        } else if (eventType === 'page') {
          map[date][site][country][device][eventType] = {
            views: 0,
            uniqueViews: 0,
          };
        }
      }

      map[date][site][country][device][eventType].views += row.views || 0;
      map[date][site][country][device][eventType].uniqueViews += row.uniqueviews || 0;
      if (eventType === 'watch') {
        map[date][site][country][device][eventType].duration += row.duration || 0;
      }

      const progress = (data.indexOf(row) / data.length) * progressRange + 20;
      job.progress = progress;
      dispatch(updateJob(job));
    }

    // Convert nested map to CSV rows
    for (const date in map) {
      for (const site in map[date]) {
        for (const country in map[date][site]) {
          for (const device in map[date][site][country]) {
            for (const eventType in map[date][site][country][device]) {
              const entry = map[date][site][country][device][eventType];
              const countryName = getCountryName(country);
              if (eventType === 'page') {
                entry.duration = 'N/A';
              }
              csv += `${date},${site},${countryName},${device},${eventType},${entry.views},${entry.duration},${entry.uniqueViews}\n`;
            }
          }
        }
      }
    }

    job.progress = 80;
    dispatch(updateJob(job));

    // export data as csv
    updateMessage(`Exporting data as CSV`);
    job.progress = 85;
    dispatch(updateJob(job));
    downloadCSV(csv, `watch-analytics-${range.from}-to-${range.to}.csv`);

    // finalize
    clearInterval(job.intervalId);
    job.progress = 100;
    job.jobStatus = 'Completed';
    dispatch(updateJob(job));
    dispatch(removeJob(job));
    return true;
  }
);

// helper fns
function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

async function processBatch(clipIds: string[], map, range) {
  const clips = await FuelPortalService.batchGet(FuelEntityType.Clip, clipIds);
  let update = '';

  for (const clip of clips) {
    const clipId = clip.id;
    let row = '';
    const match = map[clipId];
    if (match) {
      const clipDuration = clip.data?.technicalMetadata?.meta?.duration;
      // update csv with clip data
      row += `${clip.title.trim()},`;
      row += `${clipId},`;
      row += `${clip.type || ''},`;
      row += `${clip.series || ''},`;
      row += `${clip.season || ''},`;
      row += `${clipDuration},`;
      row += `${match.views},`;
      row += `${match.watchTime},`;
      row += `${match.averageWatchTime},`;
      row += `${range.from},${range.to}\n`;

      update += row;
    }
  }

  return update;
}
